import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  IconButton,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Iconify from "../../components/iconify/Iconify";
import { getAddonsKeyValue } from "../../reduxStore/slices/addonSlice";
import { formatCurrency } from "../../utils/currency";
import { useNotify } from "../../utils/snackbarUtils";
import EmptyAddonState from "./EmptyAddonState ";

const ItemAddon = ({ addons, setAddons, deletedAddons, setDeletedAddons }) => {
  const { showSuccess, handleApiError, showError } = useNotify();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const storeId = user?.storeId;

  const [allAddons, setAllAddons] = useState([]);
  const [selectedAddons, setSelectedAddons] = useState([]);

  const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
  const checkedIcon = <CheckBoxIcon fontSize="small" />;

  const getAllAddonKeyValue = async () => {
    try {
      const addons = await dispatch(getAddonsKeyValue({ storeId })).unwrap();
      setAllAddons(addons || []);
    } catch (err) {
      handleApiError(err);
    }
  };

  useEffect(() => {
    if (storeId) {
      getAllAddonKeyValue();
    }
  }, [storeId]);

  const handleAdd = () => {
    const newAddons = selectedAddons.filter(
      (addon) => !addons.find((t) => t.addon_uid === addon.addon_uid)
    );
    setAddons((prev) => [
      ...newAddons.map((a) => ({ ...a, custom_price: null, is_new_record: 1 })),
      ...prev,
    ]);
    setSelectedAddons([]);
  };

  const handleDelete = (addon_uid) => {
    const deleteAddon = addons.find((row) => row.addon_uid === addon_uid);
    if (deleteAddon && deleteAddon.product_addons_uid) {
      setDeletedAddons((prev) => [
        ...prev,
        { ...deleteAddon, record_status: "R" },
      ]);
    }
    setAddons((prev) => prev.filter((row) => row.addon_uid !== addon_uid));
  };

  const handleUserPriceChange = (addon_uid, value) => {
    setAddons((prev) =>
      prev.map((row) =>
        row.addon_uid === addon_uid
          ? { ...row, custom_price: Number(value) }
          : row
      )
    );
  };

  return (
    <Box>
      {/* Autocomplete with checkboxes */}
      <Box display="flex" gap={2} alignItems="center" sx={{ mb: 3 }}>
        <Box sx={{ width: "40%", minWidth: 300 }}>
          <Autocomplete
            limitTags={2}
            multiple
            disableCloseOnSelect
            options={allAddons}
            value={selectedAddons}
            onChange={(event, newValue) => setSelectedAddons(newValue)}
            getOptionDisabled={
              (option) => addons.some((t) => t.addon_uid === option.addon_uid) // disable already added
            }
            getOptionLabel={(option) => option.addon_name}
            renderOption={(props, option, { selected }) => {
              const isDisabled = addons.some(
                (t) => t.addon_uid === option.addon_uid
              );
              const { key, ...restProps } = props;

              return (
                <li
                  key={key}
                  {...restProps}
                  style={{ opacity: isDisabled ? 0.5 : 1 }}
                >
                  <Checkbox
                    icon={icon}
                    checkedIcon={checkedIcon}
                    style={{ marginRight: 8 }}
                    checked={selected || isDisabled} // check if selected or disabled
                    disabled={isDisabled} // disable if already added
                  />
                  {option.addon_name}
                </li>
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Addons"
                helperText={`${
                  addons.length + selectedAddons.length
                } addon(s) selected. You can add multiple.`}
              />
            )}
          />
        </Box>
        <Button
          variant="contained"
          onClick={handleAdd}
          disabled={selectedAddons.length === 0}
          sx={{ mt: "-18px", backgroundColor: "#3f51b5" }}
        >
          Add
        </Button>
      </Box>

      {/* Table */}
      {addons.length > 0 ? (
        <Table sx={{ mt: 2 }}>
          <TableHead>
            <TableRow>
              <TableCell>Addon Name</TableCell>
              <TableCell>Base Price</TableCell>
              <TableCell>Item Price</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {addons.map((row, i) => (
              <TableRow key={row.addon_uid ? row.addon_uid : i}>
                <TableCell>{row.addon_name}</TableCell>
                <TableCell>{formatCurrency(row.addon_price)}</TableCell>
                <TableCell>
                  <TextField
                    autoComplete={`addon_${row.addon_uid}`}
                    type="number"
                    size="small"
                    value={row.custom_price || ""}
                    placeholder="Enter price"
                    onChange={(e) =>
                      handleUserPriceChange(row.addon_uid, e.target.value)
                    }
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">₹</InputAdornment>
                      ),
                    }}
                    helperText="If left empty, the Base Price will be applied."
                  />
                </TableCell>
                <TableCell align="center">
                  <Tooltip title="Remove Addon">
                    <IconButton onClick={() => handleDelete(row.addon_uid)}>
                      <Iconify icon="eva:trash-2-outline" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : (
        <EmptyAddonState />
      )}
    </Box>
  );
};

export default ItemAddon;
